import { type BulkSet, parseTimeOfDay } from '@taim/shared';
import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/features/catalog/components';
import { useTagScope } from '@/features/catalog/hooks';
import { ProjectPicker, type ProjectPickerValue } from '@/features/catalog/ProjectPicker';
import { TagPicker } from '@/features/catalog/TagPicker';
import { SingleUserPicker } from '@/features/team/UserPicker';
import { useAuth } from '@/lib/auth';
import { DatePicker, errorMessage } from './inputs';

type Props = {
  /** How many entries the edit applies to (for the title). */
  count: number;
  onClose: () => void;
  /** Called with only the fields that were set. */
  onSubmit: (set: BulkSet) => void;
  pending?: boolean;
  error?: unknown;
  /** Offer the User field (people who may edit others' time). */
  allowUser?: boolean;
  weekStart?: number;
};

const formatDate = (d: string) => `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;

/**
 * Clockify's bulk edit dialog: Description, Project, Tags (add to or overwrite existing),
 * Billable, User, Time and Date. Only the fields that are set change on every selected entry.
 */
export function BulkEditDialog({ count, onClose, onSubmit, pending, error, allowUser, weekStart }: Props) {
  const fieldIds = {
    description: useId(),
    billable: useId(),
    start: useId(),
    end: useId(),
    tagMode: useId(),
  };
  /** undefined: keep; null: remove the project. */
  const [project, setProject] = useState<ProjectPickerValue | null | undefined>(undefined);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<'add' | 'replace'>('add');
  const [billable, setBillable] = useState('');
  const [description, setDescription] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const tagScope = useTagScope();
  const { tenant } = useAuth();
  /** The chosen project's client; with the projects kept, entries may be of several clients. */
  const clientId = project === undefined ? undefined : tagScope.clientOf(project?.projectId);
  const chooseProject = (next: ProjectPickerValue | null | undefined) => {
    setProject(next);
    if (next !== undefined) setTagIds((ids) => tagScope.tagsFor(ids, tagScope.clientOf(next?.projectId)));
  };

  const startMinute = start.trim() ? parseTimeOfDay(start) : undefined;
  const endMinute = end.trim() ? parseTimeOfDay(end) : undefined;
  const timeTouched = startMinute !== undefined || endMinute !== undefined;
  const timeValid = !timeTouched || (typeof startMinute === 'number' && typeof endMinute === 'number');

  const set: BulkSet = {
    ...(description.trim() ? { description: description.trim() } : {}),
    ...(project === null ? { projectId: null, taskId: null } : {}),
    ...(project ? { projectId: project.projectId, taskId: project.taskId } : {}),
    ...(tagIds.length ? { tagIds, tagMode } : {}),
    ...(billable ? { billable: billable === 'true' } : {}),
    ...(userId ? { userId } : {}),
    ...(timeTouched && timeValid ? { startMinute: startMinute!, endMinute: endMinute! } : {}),
    ...(date ? { date } : {}),
  };
  const empty = Object.keys(set).length === 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Bulk edit {count} {count === 1 ? 'entry' : 'entries'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor={fieldIds.description}>Description</Label>
            <Input
              id={fieldIds.description}
              placeholder="Keep current descriptions"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {project === null ? (
              <>
                <span className="px-2 text-muted-foreground text-sm">No project</span>
                <Button type="button" variant="link" size="sm" onClick={() => chooseProject(undefined)}>
                  Keep project
                </Button>
              </>
            ) : (
              <>
                <ProjectPicker value={project ?? null} onChange={chooseProject} allowNone={false} />
                {!tenant.requireProject && (
                  <Button type="button" variant="link" size="sm" onClick={() => chooseProject(null)}>
                    Remove project
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="grid gap-1.5">
            <TagPicker
              value={tagIds}
              clientId={clientId}
              onChange={setTagIds}
              commitOnClose={false}
              className="justify-self-start"
            />
            <fieldset className="flex gap-4 text-sm" aria-label="Tags">
              {(
                [
                  ['add', 'Add to existing'],
                  ['replace', 'Overwrite existing'],
                ] as const
              ).map(([mode, text]) => (
                <label key={mode} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={fieldIds.tagMode}
                    checked={tagMode === mode}
                    onChange={() => setTagMode(mode)}
                  />
                  {text}
                </label>
              ))}
            </fieldset>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={fieldIds.billable}>Billable</Label>
            <NativeSelect
              id={fieldIds.billable}
              value={billable}
              onChange={(e) => setBillable(e.target.value)}
            >
              <option value="">Don’t change</option>
              <option value="true">Billable</option>
              <option value="false">Non-billable</option>
            </NativeSelect>
          </div>
          {allowUser && (
            <div className="flex items-center gap-2">
              <span className="text-sm">User</span>
              <SingleUserPicker value={userId} onChange={setUserId} placeholder="User" />
            </div>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor={fieldIds.start}>Start time</Label>
              <Input
                id={fieldIds.start}
                className="w-28"
                placeholder="Keep"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={fieldIds.end}>End time</Label>
              <Input
                id={fieldIds.end}
                className="w-28"
                placeholder="Keep"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
            <DatePicker
              value={date ?? new Date().toISOString().slice(0, 10)}
              onChange={setDate}
              label={date ? formatDate(date) : 'Keep dates'}
              weekStart={weekStart}
            />
          </div>
          {!timeValid && (
            <p className="text-destructive text-sm">
              Enter both a start and an end time (e.g. 9:00 and 17:30).
            </p>
          )}
          {error ? (
            <p role="alert" className="text-destructive text-sm">
              {errorMessage(error)}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={empty || !timeValid || pending} onClick={() => onSubmit(set)}>
            SAVE
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
